import { useState } from "react";
import { roundLatLon } from "./aerialCoordinates";

export const AERIAL_IMAGE_REQUEST_SCALES = [
	{ label: "1000 ft × 1000 ft", value: 500 },
	{ label: "3000 ft × 3000 ft", value: 1500 },
];

export const DUPLICATE_IMAGE_REQUEST_MESSAGE = "An image with the same attributes already exists or has been requested.";

const formatCoordinateInput = (value) => {
	const coordinate = Number(value);
	if (!Number.isFinite(coordinate)) {
		return "";
	}
	return coordinate.toFixed(7);
};

export const hasDuplicateImageRequest = (imageRequests, lat, lon, scale) => {
	const roundedLat = roundLatLon(lat);
	const roundedLon = roundLatLon(lon);
	const numericScale = Number(scale);
	return (imageRequests || []).some(
		(imageRequest) =>
			roundLatLon(imageRequest.originLat) === roundedLat &&
			roundLatLon(imageRequest.originLon) === roundedLon &&
			Number(imageRequest.scale) === numericScale,
	);
};

const AerialImageRequestModal = ({ initialLat, initialLon, imageRequests, submitting, onSubmit, onClose }) => {
	const showCoordinates = Number.isFinite(Number(initialLat)) && Number.isFinite(Number(initialLon));
	const [latitude, setLatitude] = useState(formatCoordinateInput(initialLat));
	const [longitude, setLongitude] = useState(formatCoordinateInput(initialLon));
	const [scale, setScale] = useState(500);
	const [error, setError] = useState("");

	const submitRequest = async (event) => {
		event.preventDefault();
		if (!showCoordinates) {
			setError("");
			try {
				await onSubmit({ scale: Number(scale) });
			} catch (submitError) {
				setError(submitError.message || "Unable to submit the aerial image request.");
			}
			return;
		}
		const lat = Number(latitude);
		const lon = Number(longitude);
		if (!Number.isFinite(lat) || !Number.isFinite(lon) || lat < -90 || lat > 90 || lon < -180 || lon > 180) {
			setError("Enter a valid latitude and longitude.");
			return;
		}
		if (hasDuplicateImageRequest(imageRequests, lat, lon, scale)) {
			setError(DUPLICATE_IMAGE_REQUEST_MESSAGE);
			return;
		}
		setError("");
		try {
			await onSubmit({ lat: roundLatLon(lat), lon: roundLatLon(lon), scale: Number(scale) });
		} catch (submitError) {
			setError(submitError.message || "Unable to submit the aerial image request.");
		}
	};

	return (
		<div className="text-black fixed top-0 left-0 z-50 flex h-full w-full items-center justify-center bg-black/50" onClick={onClose}>
			<div className="m-2 w-full rounded-lg bg-white lg:w-2/5" onClick={(event) => event.stopPropagation()}>
				<div className="m-4 flex items-center justify-between">
					<p className="text-2xl font-semibold">Request new aerial image</p>
					<button type="button" className="rounded px-2 py-1 text-sm text-gray-600 hover:bg-red-500 hover:text-white" aria-label="Close" onClick={onClose}>
						Close
					</button>
				</div>
				<form className="m-4 flex flex-col gap-3" onSubmit={submitRequest}>
					{showCoordinates ? (
						<>
							<label className="text-sm font-semibold">
								Latitude
								<input
									type="number"
									step="0.0000001"
									min="-90"
									max="90"
									value={latitude}
									onChange={(event) => setLatitude(event.target.value)}
									className="mt-1 w-full rounded border border-gray-300 p-2 font-normal"
									required
								/>
							</label>
							<label className="text-sm font-semibold">
								Longitude
								<input
									type="number"
									step="0.0000001"
									min="-180"
									max="180"
									value={longitude}
									onChange={(event) => setLongitude(event.target.value)}
									className="mt-1 w-full rounded border border-gray-300 p-2 font-normal"
									required
								/>
							</label>
						</>
					) : null}
					<fieldset className="text-sm font-semibold">
						<legend>Scale</legend>
						<div className="mt-2 flex flex-col gap-2 font-normal">
							{AERIAL_IMAGE_REQUEST_SCALES.map((option) => (
								<label key={option.value} className="flex items-center gap-2">
									<input
										type="radio"
										name="aerial-image-request-scale"
										value={option.value}
										checked={Number(scale) === option.value}
										onChange={() => setScale(option.value)}
									/>
									{option.label}
								</label>
							))}
						</div>
					</fieldset>
					{error ? (
						<p role="alert" className="text-sm text-red-700">
							{error}
						</p>
					) : null}
					<div className="flex justify-end gap-2">
						<button type="button" className="rounded px-3 py-2 text-sm text-gray-600 hover:bg-gray-100" onClick={onClose} disabled={submitting}>
							Cancel
						</button>
						<button type="submit" className="rounded bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-60" disabled={submitting}>
							{submitting ? "Submitting..." : "Submit"}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
};

export default AerialImageRequestModal;
